import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ChatMessage from "@components/chat/ChatMessage";
import TypingIndicator from "@components/chat/TypingIndicator";
import {
  useConversationId,
  useInfiniteMessages,
  useSendMessage,
} from "@queries/ai.query";
import { useAuthUser } from "@stores/UseAuth";
import { uniqBy } from "lodash";
import { useNavigate } from "@tanstack/react-router";

export default function ChatPage() {
  const { data: conversationId, isLoading: isInitializing } =
    useConversationId();
  const user = useAuthUser((state) => state.user);
  const userAvatar = user?.socialite_avatar || undefined;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteMessages(conversationId ?? null, 10);
  const sendMutation = useSendMessage(conversationId ?? null);

  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamTimerRef = useRef<number | null>(null);
  const anchorRef = useRef<{ prevScrollHeight: number | null; lock: boolean }>({
    prevScrollHeight: null,
    lock: false,
  });
  const shouldAutoScrollRef = useRef<boolean>(true);

  const history = useMemo(() => {
    if (!data?.pages) return [] as any[];
    return data.pages.flatMap((p: any) => p || []);
  }, [data?.pages]);

  // Chỉ prevent body scroll cơ bản
  useEffect(() => {
    const originalBodyOverflow = window.getComputedStyle(
      document.body
    ).overflow;
    const originalHtmlOverflow = window.getComputedStyle(
      document.documentElement
    ).overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    const map = new Map<string, any>();
    const push = (arr: any[]) =>
      arr.forEach((m) => {
        if (m && m._id && !map.has(m._id)) map.set(m._id, m);
      });
    push(history);
    push(messages);
    const merged = Array.from(map.values()).sort(
      (a: any, b: any) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    );
    setMessages(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [isInitializing, isLoading]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (anchorRef.current.lock) {
      const prev = anchorRef.current.prevScrollHeight ?? el.scrollHeight;
      const delta = el.scrollHeight - prev;
      el.scrollTop = delta;
      anchorRef.current.lock = false;
      anchorRef.current.prevScrollHeight = null;
      return;
    }
    if (shouldAutoScrollRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearTop = el.scrollTop <= 0;
    const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 20;
    shouldAutoScrollRef.current = nearBottom;
    if (nearTop && hasNextPage && !isFetchingNextPage && !isTyping) {
      anchorRef.current.prevScrollHeight = el.scrollHeight;
      anchorRef.current.lock = true;
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isTyping, fetchNextPage]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || !conversationId) return;
    const content = input.trim();
    setInput("");
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.overflowY = "hidden";
    }

    const optimistic: any = {
      _id: Date.now().toString(),
      content,
      updatedAt: new Date().toISOString(),
      conversationId,
      role: "user",
      __v: 0,
      avatar: userAvatar || "/images/default_image.png",
    };
    setMessages((prev) => [...prev, optimistic]);
    setIsTyping(true);
    shouldAutoScrollRef.current = true;

    sendMutation.mutate(content, {
      onSuccess: (res) => {
        const r: any = res || {};
        const payload = (r.response?.data ??
          r.response?.data?.data ??
          r.data ??
          r.data?.data) as unknown;
        const list = Array.isArray(payload) ? payload : [];
        const assistant = list[1] || [];
        if (!assistant) return;
        const placeholder = { ...assistant, content: "" };
        setMessages((prev) => [...prev, placeholder]);
        const words = String(assistant.content || "").split(/(\s+)/);
        let idx = 0;
        const step = () => {
          idx = Math.min(idx + Math.floor(Math.random() * 2) + 1, words.length);
          const nextText = words.slice(0, idx).join("");
          setMessages((prev) =>
            prev.map((m) =>
              m._id === assistant._id ? { ...m, content: nextText } : m
            )
          );
          const sc = scrollRef.current;
          if (sc) {
            const distance = sc.scrollHeight - (sc.scrollTop + sc.clientHeight);
            if (distance < 160) sc.scrollTop = sc.scrollHeight;
          }
          if (idx < words.length) {
            streamTimerRef.current = window.setTimeout(
              step,
              Math.random() * 50 + 30
            );
          }
        };
        step();
        shouldAutoScrollRef.current = true;
      },
      onSettled: () => setIsTyping(false),
    });
  }, [input, conversationId, sendMutation, userAvatar]);

  if (isInitializing) {
    return (
      <div className="flex flex-col bg-white h-screen">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Spinner label="Đang khởi tạo cuộc trò chuyện..." />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col bg-white chat_container"
      style={{ height: "100dvh" }}
    >
      <Header />
      {isLoading ? (
        <div className="h-full flex items-center justify-center">
          <Spinner label="Đang tải tin nhắn..." />
        </div>
      ) : (
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto px-4 py-3"
          style={{
            minHeight: 0,
            paddingBottom: 80,
            paddingTop: 66,
          }}
        >
          <div className="flex flex-col max-w-4xl mx-auto w-full">
            {uniqBy(messages, "_id")
              .slice()
              .map((m: any) => (
                <ChatMessage
                  key={m._id}
                  message={m}
                  isUser={m.role === "user"}
                  userAvatar={userAvatar}
                />
              ))}
            {isTyping && <TypingIndicator />}
            {hasNextPage && (
              <div className="text-center text-xs text-gray-500 py-2">
                {isFetchingNextPage && <Spinner label="Đang tải thêm..." />}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className="border-t border-gray-200 p-3"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "#fff",
        }}
      >
        <div className="max-w-4xl mx-auto w-full flex items-end gap-2 bg-[#f5faff] border border-blue-500 rounded-xl px-3 py-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onInput={(e) => {
              const el = e.currentTarget as HTMLTextAreaElement;
              el.style.height = "auto";
              const h = Math.min(el.scrollHeight, 200);
              el.style.height = h + "px";
              el.style.overflowY = el.scrollHeight > 200 ? "auto" : "hidden";
              const sc = scrollRef.current;
              if (sc) {
                const nearBottom =
                  sc.scrollHeight - (sc.scrollTop + sc.clientHeight) < 24;
                if (nearBottom) sc.scrollTop = sc.scrollHeight;
              }
            }}
            placeholder="Nhập tin nhắn"
            className="flex-1 bg-transparent outline-none resize-none text-base text-gray-800 leading-6 py-1"
            style={{ maxHeight: 200 }}
            rows={1}
            onFocus={(e) => {
              // Scroll content xuống bottom khi input được focus
              setTimeout(() => {
                const scrollContainer = scrollRef.current;
                if (scrollContainer) {
                  scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }
              }, 300);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className={`w-10 h-8 rounded-full flex items-center justify-center ${input.trim() ? "text-blue-600" : "text-gray-400"}`}
            aria-label="Gửi"
            title="Gửi"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function Header() {
  const navigate = useNavigate();
  return (
    <div
      className="border-b border-gray-200 bg-white flex-shrink-0  z-50"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}
    >
      <div className="max-w-4xl mx-auto w-full flex items-center justify-between px-2 py-2">
        <button
          aria-label="Back"
          onClick={() => navigate({ to: "/" })}
          className="w-10 h-10 flex items-center justify-center text-gray-700"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M15 18l-6-6 6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="flex-1 text-center text-blue-600 font-semibold">
          Trợ lý AI11
        </div>
        <div className="w-10" />
      </div>
    </div>
  );
}

function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-600">
      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}
